import { Application } from "@hotwired/stimulus";
import { definitionsFromContext } from "@hotwired/stimulus-webpack-helpers";
import Lenis from "@studio-freight/lenis";
import * as CoreControllers from "@core-js/controllers/index.js";

// ── Backwards compat: migrate modujs data-module-* → data-controller ──
// Run BEFORE Application.start() so Stimulus picks up the converted attributes.
// Remove this block once all PHP templates use data-controller directly.
(function migrateModujsToStimulus() {
	const map = {
		menubutton: "menu-button",
		menumobile: "mobile-menu",
		totop: "totop",
		flash: "flash",
		more: "load-more",
		searchbox: "searchbox",
		multimedia: "multimedia",
		firma: "firma",
		printcard: "print-card",
		cart: "cart",
		search: "search",
	};

	for (const [oldName, newName] of Object.entries(map)) {
		document.querySelectorAll(`[data-module-${oldName}]`).forEach((el) => {
			const existing = el.getAttribute("data-controller") || "";
			el.setAttribute(
				"data-controller",
				[existing, newName].filter(Boolean).join(" "),
			);
		});
	}
})();

// ── Stimulus: register core + auto-discover theme controllers ──

const application = Application.start();
window.Stimulus = application;

// Core controllers: imported as barrel (always included, no tree-shaking)
Object.entries(CoreControllers).forEach(([name, module]) => {
	const identifier = name.replace("_controller", "").replace(/_/g, "-");
	application.register(identifier, module);
});

// Theme-local controllers: official Stimulus webpack autoload.
// Create a file like ./controllers/my_block_controller.js and it registers
// automatically as data-controller="my-block". Nested paths become --.
const themeControllersContext = require.context(
	"./controllers",
	true,
	/_controller\.js$/,
);
application.load(definitionsFromContext(themeControllersContext));

// ── Lenis: smooth scroll ──
const lenis = new Lenis({
	duration: 1,
	easing: (t) => 1 - (1 - t) ** 3,
	direction: "vertical",
	smooth: true,
	smoothTouch: true,
	touchMultiplier: 2,
});
window.lenis = lenis;

// ── Sincronizar ScrollTrigger con Lenis ──
// Lenis intercepta el scroll nativo, ScrollTrigger necesita que le avisen.
// Usamos window.ScrollTrigger (expuesto globalmente por GSAP) para
// no requerir import directo de gsap/ScrollTrigger en este bundle.
lenis.on("scroll", () => {
	if (window.ScrollTrigger) {
		window.ScrollTrigger.update();
	}
});

// Pausar/reanudar Lenis cuando body tiene .no-scroll
const bodyObserver = new MutationObserver((mutations) => {
	mutations.forEach((mutation) => {
		if (mutation.type === "attributes" && mutation.attributeName === "class") {
			const hasNoScroll = document.body.classList.contains("no-scroll");
			if (hasNoScroll) {
				lenis.stop();
			} else {
				lenis.start();
			}
		}
	});
});
bodyObserver.observe(document.body, {
	attributes: true,
	attributeFilter: ["class"],
});

// Maneja la clase del header según el estado de scroll
function handleHeaderScroll(lenis) {
	const headerElement = document.querySelector("header");
	if (!headerElement) return;
	const isHomepage = document.body.classList.contains("home");
	const nav = document.querySelector(".theme-nav");
	let navIsOpen = nav?.classList.contains("open");
	if (!isHomepage && !navIsOpen) {
		headerElement.classList.add("header--scrolled");
	}
	lenis.on("scroll", ({ scroll }) => {
		navIsOpen = nav?.classList.contains("open");
		if (isHomepage) {
			if (scroll > 20 && !navIsOpen) {
				headerElement.classList.add("header--scrolled");
			} else if (scroll <= 20 || navIsOpen) {
				headerElement.classList.remove("header--scrolled");
			}
		}
	});
}

// Scroll suave hacia un anchor (usa Lenis)
function smoothScrollToAnchor(anchor, lenis) {
	if (!anchor) return;
	const targetElement = document.querySelector(anchor);
	if (targetElement) {
		try {
			targetElement.focus({ preventScroll: true });
		} catch (e) {
			try {
				targetElement.focus();
			} catch (e2) {
				/* noop */
			}
		}
		const headerHeight = document.querySelector("header")?.offsetHeight || 0;
		const scrollY = window.scrollY || window.pageYOffset || 0;
		const targetPosition = targetElement.getBoundingClientRect().top + scrollY;
		lenis.scrollTo(targetPosition - headerHeight, {
			duration: 2.5,
			easing: (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
		});
	}
}

// Maneja links de ancla con Lenis
function handleAnchorLinks(lenis) {
	window.addEventListener("hashchange", () => {
		if (window.location.hash) {
			smoothScrollToAnchor(window.location.hash, lenis);
		}
	});
	document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
		anchor.addEventListener("click", (event) => {
			event.preventDefault();
			const targetId = anchor.getAttribute("href");
			smoothScrollToAnchor(targetId, lenis);
		});
	});
}

function initScroll(lenis) {
	handleHeaderScroll(lenis);
	handleAnchorLinks(lenis);
}

// Debounce global para ScrollTrigger.refresh()
let scrollTriggerRefreshTimer = null;
function scheduleScrollTriggerRefresh(delay = 200) {
	if (!window.ScrollTrigger) return;
	if (scrollTriggerRefreshTimer) {
		clearTimeout(scrollTriggerRefreshTimer);
	}
	scrollTriggerRefreshTimer = setTimeout(() => {
		scrollTriggerRefreshTimer = null;
		window.ScrollTrigger.refresh();
	}, delay);
}

// RAF loop para Lenis y lazySizes
function raf(time) {
	lenis.raf(time);
	if (window.lazySizes && window.lazySizes.loader) {
		window.lazySizes.loader.checkElems();
	}
	requestAnimationFrame(raf);
}

// Recalcular Lenis cuando cambia el tamaño del documento
function observeScrollContainer(lenis) {
	const scrollEl = document.scrollingElement || document.documentElement;
	const targets = [document.body, document.documentElement, scrollEl].filter(
		Boolean,
	);
	const resizeObserver = new ResizeObserver(() => {
		lenis.resize();
		scheduleScrollTriggerRefresh();
	});
	targets.forEach((element) => resizeObserver.observe(element));

	let prevScrollHeight = document.documentElement.scrollHeight;
	lenis.on("scroll", () => {
		const scrollHeight = document.documentElement.scrollHeight;
		if (scrollHeight !== prevScrollHeight) {
			prevScrollHeight = scrollHeight;
			lenis.resize();
			scheduleScrollTriggerRefresh();
		}
	});
	window.addEventListener(
		"resize",
		() => {
			lenis.resize();
			scheduleScrollTriggerRefresh();
		},
		{ passive: true },
	);
}

function initOnLoad() {
	initScroll(lenis);
	observeScrollContainer(lenis);
	scheduleScrollTriggerRefresh(350);
	requestAnimationFrame(raf);
}

// ── DOM Ready ──
document.addEventListener("DOMContentLoaded", () => {
	if (window.location.hash) {
		const targetId = window.location.hash.substring(1);
		const targetElement = document.getElementById(targetId);
		if (targetElement) {
			window.history.replaceState(null, null, " ");
			setTimeout(() => {
				smoothScrollToAnchor(`#${targetId}`, lenis);
				window.history.replaceState(null, null, `#${targetId}`);
			}, 2000);
		}
	}

	// Accessibility: make button-like anchors without href focusable
	document.querySelectorAll(".wp-block-button__link").forEach((el) => {
		if (!el.hasAttribute("href")) {
			el.setAttribute("tabindex", "0");
			if (!el.hasAttribute("role")) el.setAttribute("role", "button");
		}
	});

	// Accessibility: make mini-cart drawer inert when closed
	function handleMiniCartInert() {
		const FOCUSABLE =
			'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex]:not([tabindex="-1"]), [contenteditable]';

		function inertify(drawer) {
			if (!drawer) return;
			const focusables = drawer.querySelectorAll(FOCUSABLE);
			if (
				typeof HTMLElement !== "undefined" &&
				"inert" in HTMLElement.prototype
			) {
				try {
					drawer.inert = true;
				} catch (e) {
					/* noop */
				}
			} else {
				drawer.setAttribute("inert", "");
			}
			focusables.forEach((el) => {
				const prev = el.getAttribute("tabindex");
				if (prev !== null) el.setAttribute("data-prev-tabindex", prev);
				el.setAttribute("tabindex", "-1");
			});
			drawer.setAttribute("data-inertified", "true");
		}

		function deinertify(drawer) {
			if (!drawer) return;
			if (
				typeof HTMLElement !== "undefined" &&
				"inert" in HTMLElement.prototype
			) {
				try {
					drawer.inert = false;
				} catch (e) {
					/* noop */
				}
			} else {
				drawer.removeAttribute("inert");
			}
			if (!drawer.hasAttribute("data-inertified")) return;
			const focusables = drawer.querySelectorAll('[tabindex="-1"]');
			focusables.forEach((el) => {
				const prev = el.getAttribute("data-prev-tabindex");
				if (prev !== null) {
					el.setAttribute("tabindex", prev);
					el.removeAttribute("data-prev-tabindex");
				} else {
					el.removeAttribute("tabindex");
				}
			});
			drawer.removeAttribute("data-inertified");
		}

		function processDrawer(drawer) {
			if (!drawer) return;
			const ariaHidden = drawer.getAttribute("aria-hidden");
			if (ariaHidden === "true" || drawer.classList.contains("is-hidden")) {
				inertify(drawer);
			} else {
				deinertify(drawer);
			}
		}

		const observed = new WeakSet();
		function observe(drawer) {
			if (!drawer || observed.has(drawer)) return;
			observed.add(drawer);
			processDrawer(drawer);
			const mo = new MutationObserver((mutations) => {
				for (const m of mutations) {
					if (
						m.type === "attributes" &&
						(m.attributeName === "aria-hidden" || m.attributeName === "class")
					) {
						processDrawer(drawer);
					}
				}
			});
			mo.observe(drawer, {
				attributes: true,
				attributeFilter: ["aria-hidden", "class"],
			});
		}

		document.querySelectorAll(".wc-block-mini-cart__drawer").forEach(observe);
		const bodyObserver = new MutationObserver((mutations) => {
			for (const m of mutations) {
				m.addedNodes?.forEach((node) => {
					if (node.nodeType === 1) {
						if (node.matches(".wc-block-mini-cart__drawer")) observe(node);
						node.querySelectorAll &&
							node
								.querySelectorAll(".wc-block-mini-cart__drawer")
								.forEach(observe);
					}
				});
			}
		});
		bodyObserver.observe(document.body, { childList: true, subtree: true });
	}

	handleMiniCartInert();
	initOnLoad();
});
